// src/pages/PublicationDetail.jsx - UPDATED WITH IMAGE
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Calendar, Clock, ArrowLeft, ExternalLink, MessageCircle } from 'lucide-react';
import publicationsData from '../data/publications.json';
import categoriesData from '../data/categories.json';

const PublicationDetail = () => {
  const { id } = useParams();
  const [publication, setPublication] = useState(null);
  const [content, setContent] = useState('');
  const [relatedPublications, setRelatedPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const loadPublication = async () => {
      setLoading(true);
      
      try {
        // Find publication metadata
        const pub = publicationsData.find(p => p.id === id);
        if (!pub) {
          setLoading(false);
          return;
        }

        setPublication(pub);

        // Load markdown content from public folder
        try {
          // Use process.env.PUBLIC_URL for proper path in both dev and production
          const response = await fetch(`${process.env.PUBLIC_URL}/publications/content/${id}.md`);
          if (!response.ok) {
            throw new Error(`Failed to load markdown file: ${response.status}`);
          }
          const text = await response.text();
          setContent(text);
        } catch (error) {
          console.error('Error loading markdown:', error);
          // Fallback content
          setContent(`# ${pub.title}\n\n${pub.excerpt}\n\n*This article explores ${pub.title.toLowerCase()} in depth. For more detailed discussion or implementation guidance, feel free to reach out.*`);
        }

        // Find related publications (same category)
        const related = publicationsData
          .filter(p => p.id !== id && p.category === pub.category)
          .slice(0, 3);
        setRelatedPublications(related);
      } catch (error) {
        console.error('Error loading publication:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPublication();
  }, [id]);

  if (loading) {
    return (
      <div className="full-section">
        <div className="loading">
          <div className="loading-spinner"></div>
          Loading publication...
        </div>
      </div>
    );
  }

  if (!publication) {
    return (
      <div className="full-section">
        <div className="not-found">
          <h1>Publication not found</h1>
          <Link to="/publications" className="btn-primary">
            <ArrowLeft size={16} />
            Back to Publications
          </Link>
        </div>
      </div>
    );
  }

  const category = categoriesData.find(c => c.name === publication.category);
  const imagePath = `/images/publications/${id}/main.png`;

  return (
    <section className="full-section publication-detail">
      <div className="publication-container">
        <Link to="/publications" className="back-link">
          <ArrowLeft size={16} />
          Back to Publications
        </Link>
        
        <div className="publication-header">
          <div className="publication-meta-header">
            <div className="category-tag" style={{ 
              backgroundColor: category?.color + '20', 
              color: category?.color 
            }}>
              <span className="category-icon">{category?.icon}</span>
              {publication.category}
            </div>
            <div className="publication-date">
              <Calendar size={14} />
              {new Date(publication.date).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </div>
          </div>

          <h1>{publication.title}</h1>
          <p className="publication-excerpt">{publication.excerpt}</p>
          
          <div className="publication-stats">
            <div className="stat-item">
              <Clock size={14} />
              <span>{publication.readTime}</span>
            </div>
            <div className="tags">
              {publication.tags.map(tag => (
                <span key={tag} className="tag">{tag}</span>
              ))}
            </div>
          </div>
        </div>

        <div className="publication-content-container">
          {/* Publication Image */}
          <div className="publication-image-large">
            <div className="image-wrapper">
              <img 
                src={imagePath} 
                alt={publication.title}
                className="publication-main-image"
                onError={() => setImageError(true)}
              />
              {imageError && (
                <div className="image-fallback-large">
                  <div className="fallback-content">
                    <div className="fallback-icon">{category?.icon}</div>
                    <h3>{publication.category}</h3>
                    <p>{publication.title}</p>
                  </div>
                </div>
              )}
            </div>
            {!imageError && (
              <div className="image-caption">
                Featured image for {publication.title.toLowerCase()}
              </div>
            )}
          </div>

          {/* Article Content */}
          <article className="markdown-content">
            <ReactMarkdown>{content}</ReactMarkdown>
          </article>
          {/* Related Publications */}
          {relatedPublications.length > 0 && (
            <div className="related-publications">
              <h3>Related Articles</h3>
              <div className="related-grid">
                {relatedPublications.map(pub => {
                  const relatedCategory = categoriesData.find(c => c.name === pub.category);
                  const relatedImagePath = `/images/publications/${pub.id}/main.png`;
                  
                  return (
                    <Link key={pub.id} to={`/publications/${pub.id}`} className="related-card">
                      <div className="related-card-image">
                        <img 
                          src={relatedImagePath} 
                          alt={pub.title}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'flex';
                          }}
                        />
                        <div className="related-image-fallback" style={{ backgroundColor: relatedCategory?.color + '20' }}>
                          <span>{relatedCategory?.icon}</span>
                        </div>
                      </div>
                      <div className="related-card-content">
                        <div className="related-category" style={{ color: relatedCategory?.color }}>
                          {relatedCategory?.icon} {pub.category}
                        </div>
                        <h4>{pub.title}</h4>
                        <p>{pub.excerpt}</p>
                        <div className="related-meta">
                          <span>{pub.readTime}</span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PublicationDetail;